import { Intercom } from '@capacitor-community/intercom';
import { isPlatform } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useCurrentUser } from '../api/user.hooks';
import { useUserProvider } from '../api/user.provider';
import {
  IntercomExtension,
  IntercomExtensionEvent,
  IntercomUnreadNotificationCountDidChangeEvent,
} from '../plugins/intercom.plugin';
import { logger } from '../util/logger';
import { getPlatform } from '../util/platform';
import { VERSION } from '../version';
import { ApplicationConfig } from './config';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Intercom: (action: string, ...args: any) => void;
  }
}

interface IntercomCompany {
  companyId: string;
  name: string;
}

let webIntercomAvailable = false;

export enum IntercomArticle {
  AI_RECOMMENDATIONS = '8219272',
  APP_CUTTING_EDGE_START = '8338526',
  DIRECT_CONNECT_OVERVIEW = '9544498',
  ENABLING_PUSH_NOTIFICATIONS = '6116683',
  ENHANCED_STITCH_WELD = '9709711',
  GETTING_STARTED = '6015503',
  INTEGRATE_WITH_EXTERNAL_EQUIPMENT = '5721980',
  MAINTENANCE_WINDOWS = '8719427',
  MANAGING_ROBOTS = '5787399',
  MULTI_EDIT_CUT_SETTINGS = '8531865',
  MULTI_EDIT_WELD_SETTINGS = '5336609',
  MULTIPASS_WELD = '11394709',
  OPERATOR_ROLE = '6492887',
  PLAYLIST_OVERVIEW = '5324290',
  PRO_PLAN = '6365311',
  PRO_PLAN_PART_ATTACHMENTS = '6396005',
  SEAM_TRACKING = '11825849',
  SHIFTING = '7050441',
}

export const getIntercomArticleURL = (articleId: IntercomArticle): string => {
  return `${ApplicationConfig.intercom.articlesUrl}/${articleId}`;
};

export const isIntercomAvailable = async (): Promise<boolean> => {
  if (isPlatform('capacitor')) {
    return checkIntercomMobile();
  } else {
    return webIntercomAvailable;
  }
};

const checkIntercomMobile = async (): Promise<boolean> => {
  try {
    await IntercomExtension.unreadConversationCount();
  } catch (e) {
    return false;
  }

  return true;
};

export const useInjectWebIntercom = (onError?: () => void): boolean => {
  const [injected, setInjected] = useState<boolean>(false);

  useEffect(() => {
    if (isPlatform('capacitor')) {
      return;
    }

    const scriptId = 'intercom-script';
    const el = document.getElementById(scriptId);
    let owner = false;

    if (!el) {
      const script = document.createElement('script');

      script.id = scriptId;
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://widget.intercom.io/widget/ixviut62`;
      script.onload = () => {
        setInjected(true);
        webIntercomAvailable = true;
      };

      script.onerror = () => {
        setInjected(false);
        if (typeof onError === 'function') {
          onError();
        }
      };

      const parent = document.getElementsByTagName('head')[0];
      parent.appendChild(script);

      owner = true;

      return () => {
        if (el && owner) {
          document.removeChild(el);
        }
      };
    }
  }, [onError]);

  return injected;
};

const useIntercomLogout = (): (() => Promise<void>) => {
  return useCallback(async () => {
    if (isPlatform('capacitor')) {
      await Intercom.logout();
    } else {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    }
  }, []);
};

const useLaunchIntercomArticle = (): ((
  articleId: IntercomArticle
) => Promise<void>) => {
  return useCallback(async (articleId: string) => {
    if (isPlatform('capacitor')) {
      try {
        await Intercom.displayArticle({ articleId });
      } catch (error) {
        logger.error('failed to display article', { error, articleId });
      }
    } else {
      window.Intercom('showArticle', articleId);
    }
  }, []);
};

const useIntercomUnreadNotificationCount = (): number => {
  const [count, setCount] = useState<number>(0);

  // effect for capacitor
  useEffect(() => {
    let listenerRegistered = false;

    if (isPlatform('capacitor')) {
      IntercomExtension.addListener(
        IntercomExtensionEvent.UNREAD_CONVERSATION_COUNT_DID_CHANGE,
        async (event: IntercomUnreadNotificationCountDidChangeEvent) => {
          setCount(event.count);
        }
      );

      listenerRegistered = true;
    }

    return () => {
      if (listenerRegistered) {
        IntercomExtension?.removeAllListeners();
      }
    };
  }, []);

  // effect for web
  useEffect(() => {
    if (!isPlatform('capacitor') && window.Intercom) {
      window.Intercom('onUnreadCountChange', (newCount: number) => {
        setCount(newCount);
      });
    }
  }, []);

  return count;
};

const useInitializeIntercom = (): void => {
  const [booted, setBooted] = useState<boolean>(false);
  const [registered, setRegistered] = useState<boolean>(false);
  const userObj = useCurrentUser();
  const { userInfo } = useUserProvider();

  const webInjected = useInjectWebIntercom(() => {
    webIntercomAvailable = false;
  });

  useEffect(() => {
    if (!booted) {
      if (!isPlatform('capacitor')) {
        if (webInjected) {
          // Wait for user data before booting with user_hash
          if (userObj && userInfo && userObj.intercomUserHash) {
            try {
              window.Intercom('boot', {
                app_id: ApplicationConfig.intercom.appId,
                user_hash: userObj.intercomUserHash,
              });
              setBooted(true);
            } catch (error) {
              logger.error('failed to boot intercom with user data', {
                userInfo,
                error,
              });
            }
          } else {
            // Boot without user_hash if user data not available yet
            try {
              window.Intercom('boot', {
                app_id: ApplicationConfig.intercom.appId,
              });
              setBooted(true);
            } catch (error) {
              logger.error('failed to boot intercom without user data', {
                userId: userObj?.id,
                error,
              });
            }
          }
        }
      } else {
        setBooted(true);
      }
    }
  }, [webInjected, booted, userObj, userInfo]);

  useEffect(() => {
    if (userObj && userInfo && booted) {
      const {
        admin,
        email,
        firstName,
        lastName,
        intercomUserHash,
        billingManager,
      } = userObj ?? {};

      const { tenantId, tenantName } = userInfo;

      if (intercomUserHash) {
        const name =
          firstName && lastName
            ? `${firstName.trim()} ${lastName.trim()}`
            : undefined;

        if (isPlatform('capacitor')) {
          const companies: IntercomCompany[] = [];

          if (userInfo) {
            companies.push({
              companyId: tenantId,
              name: tenantName ?? 'unknown',
            });
          }

          const setup = async () => {
            const user = {
              name,
              email,
              companies,
              customAttributes: {
                env: ApplicationConfig.env,
                admin,
                'Billing Manager': billingManager,
                'Beacon Version': VERSION,
              },
            };

            if (!registered) {
              logger.info(
                'user not registered in intercom, setting user hash and registering user',
                {
                  platform: getPlatform(),
                  user,
                }
              );

              try {
                await Intercom.setUserHash({ hmac: intercomUserHash });
              } catch (error) {
                logger.error('failed to set intercom user hash', {
                  user,
                  platform: getPlatform(),
                  error,
                });
                return;
              }

              try {
                await Intercom.registerIdentifiedUser(user);
              } catch (error) {
                logger.error('failed to register intercom user', {
                  user,
                  platform: getPlatform(),
                  error,
                });
                return;
              }

              setRegistered(true);
            } else {
              try {
                await IntercomExtension.updateUser(user);
              } catch (error) {
                logger.error('failed to update intercom user', {
                  user,
                  platform: getPlatform(),
                  error,
                });
              }
            }
          };
          setup();
        } else {
          const company = tenantId
            ? {
                company_id: tenantId,
                name: tenantName ?? 'unknown',
              }
            : undefined;

          try {
            window.Intercom('update', {
              name,
              email,
              company,
              env: ApplicationConfig.env,
              admin,
              'Billing Manager': billingManager,
              'Beacon Version': VERSION,
            });
          } catch (error) {
            logger.error('failed to update intercom user', {
              email,
              tenantId,
              platform: getPlatform(),
              error,
            });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userObj), JSON.stringify(userInfo), registered, booted]);
};

const trackIntercomEvent = async (
  eventName: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  try {
    if (isPlatform('capacitor')) {
      await Intercom.logEvent({
        name: eventName,
        data: metadata,
      });
    } else {
      await window.Intercom('trackEvent', eventName, metadata);
    }
  } catch (error) {
    // do nothing
  }
};

const displayIntercomMessenger = () => {
  const isCapacitor = isPlatform('capacitor');

  if (isCapacitor) {
    Intercom.displayMessenger();
  } else {
    try {
      window.Intercom('show');
    } catch (error) {
      logger.error('failed to show intercom messenger', {
        platform: getPlatform(),
        error,
      });
    }
  }
};

export {
  displayIntercomMessenger,
  trackIntercomEvent,
  useInitializeIntercom,
  useIntercomLogout,
  useIntercomUnreadNotificationCount,
  useLaunchIntercomArticle,
};
